import {
  EBeneficiaryType,
  ECreditApplicationStatus,
  EDecision,
  EFinancingProgram,
  EProvince,
  getEProvinceList,
} from '@if/core/types'
import { Alert, AlertTitle } from '@mui/material'
import PageSpinner from '@src/components/PageSpinner'
import { useUser } from '@src/contexts/AppContext'
import { useSaveCreditApplicationDraft } from '@src/data/api/credit-api/credit-api'
import { useMerchantById } from '@src/data/api/merchants-api/merchants-api'
import { useQuoteById } from '@src/data/api/quote-api/quote-api'
import useFeatureFlags from '@src/hooks/config-hooks'
import { Merchant } from '@src/types/Merchant'
import { CreditApplication, DraftCreditApplicationSchema } from '@src/types/CreditApplicationSchema'
import { QuoteApplicationPrefill } from '@src/types/QuoteApplicationPrefill'
import { useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { adjustCreditApplicationYearsForApi } from '../ViewCreditApplicationPage/credit-hooks'
import CreditApplicationForm from './components/CreditApplicationForm'

const PROVINCES_BY_CODE = new Map<string, EProvince>(
  getEProvinceList().map((province) => [province.toUpperCase(), province]),
)

// Merges quote pre-fill data (#66104) over a fresh draft. Only the fields with an
// unambiguous 1:1 match are applied — merchant/financingProgram/income/expenses stay
// driven by the route and the dealer's own entry, never by the quote.
//
// The quote never supplies a civicNumber (BrokerGateway's address shape doesn't have
// one), so a prefilled address will trip AddressSchema's conditional civicNumber
// requirement and the dealer will see it as a required field to complete — expected,
// not a bug: the AC accepts that the dealer reviews/completes the form before submitting.
const buildInitialCreditApplication = (quote: QuoteApplicationPrefill | null): CreditApplication => {
  const draft = DraftCreditApplicationSchema().getDefault() as unknown as CreditApplication
  if (!quote) return draft

  // The quote's province string isn't guaranteed to match an EProvince member exactly;
  // an unvalidated value could silently defeat province-specific logic (e.g. Quebec
  // consent handling) before the form's own validation ever runs. Matched case-insensitively
  // since the code's casing isn't guaranteed either.
  const stateIso = PROVINCES_BY_CODE.get(quote.currentAddress.stateIso.toUpperCase()) ?? null

  return {
    ...draft,
    requestedLoanAmount: quote.requestedLoanAmount,
    applicant: {
      ...draft.applicant,
      firstName: quote.firstName,
      lastName: quote.lastName,
      birthDate: quote.birthDate,
      cellPhone: quote.cellPhone,
      email: quote.email,
      currentAddress: {
        ...draft.applicant.currentAddress,
        ...quote.currentAddress,
        stateIso,
      },
    },
  }
}

interface CreateCreditApplicationFormProps {
  merchant: Merchant | null
  financingProgramId: EFinancingProgram
  quote: QuoteApplicationPrefill | null
}

// Mounted only once the quote fetch (if any) has resolved, so its useState initializer
// always sees the final quote value instead of the in-flight `null`.
const CreateCreditApplicationForm = ({
  merchant,
  financingProgramId,
  quote,
}: CreateCreditApplicationFormProps): JSX.Element => {
  const [saveCreditAppMutation, isPending, isError, reset, error] = useSaveCreditApplicationDraft()
  const [creditApplication] = useState<CreditApplication>(() => buildInitialCreditApplication(quote))
  const navigate = useNavigate()

  const onSave = async (data: Partial<CreditApplication>) => {
    if (!data.beneficiaryTypeId) data.beneficiaryTypeId = EBeneficiaryType.Applicant
    adjustCreditApplicationYearsForApi(data)
    await saveCreditAppMutation(data).then((creditApp) => {
      reset()
      if (creditApp.status === ECreditApplicationStatus.Draft) navigate(`/`)
      else {
        navigate(`/credits/${creditApp.financingProgramId}/${creditApp.id}`)
      }
    })
  }
  const isPrequalComplete =
    creditApplication?.prequalificationDecision?.decision === EDecision.Approved ||
    (creditApplication?.prequalificationDecision?.decision === EDecision.Refused &&
      creditApplication?.applicant?.hasCreditReport === true)

  return (
    <CreditApplicationForm
      merchant={merchant}
      creditApplication={creditApplication}
      onSave={onSave}
      isPending={isPending}
      isError={isError}
      serverErrors={error}
      financingProgramId={financingProgramId}
      isPrequalComplete={isPrequalComplete}
      isApplicantSinNeeded={false}
      isCoapplicantSinNeeded={false}
    />
  )
}

const CreateCreditApplicationPage = (): JSX.Element => {
  const { t } = useTranslation()
  const user = useUser()
  const { financingProgramId } = useParams<{ financingProgramId: EFinancingProgram }>()
  const [merchant] = useMerchantById(user?.merchantId ?? '')
  const { isFetchingFeatureSwitch } = useFeatureFlags()

  const [searchParams] = useSearchParams()
  const quoteId = searchParams.get('quoteId')
  const [quote, isFetchingQuote, isQuoteError] = useQuoteById(quoteId)

  return (
    <div>
      {isFetchingQuote && <PageSpinner isLoading />}
      {isQuoteError && (
        <Alert sx={{ mb: 2 }} severity="warning">
          <AlertTitle>{t('common.error')}</AlertTitle>
          {t('common.errors.quotePrefillFailed')}
        </Alert>
      )}
      {!isFetchingFeatureSwitch && !isFetchingQuote && (
        <CreateCreditApplicationForm merchant={merchant} financingProgramId={financingProgramId!} quote={quote} />
      )}
    </div>
  )
}

export default CreateCreditApplicationPage
